import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TransfersFilter, TransfersResponse } from "../../modules/Transfers/types/transfers-response";
import { transfersReqAPI } from "../../modules/Transfers/api/transfersAPI";
import { useCustomToast } from "../../components";
import { DownloadFileReqAPI } from "../../share/api/downloadFileService";

// Query key for caching
const keyQuery = ['transfers'] as const;

export const useFetchTransferRequests = (filters?: Partial<TransfersFilter>) => {
  return useQuery<TransfersResponse, Error>({
    queryKey: [keyQuery, filters || {}],
    queryFn: ({ queryKey }) => transfersReqAPI.gets({ queryKey }),
    enabled: true,
  });
};

// Fetch a single transfer request by ID
export const useFetchTransferById = (id?: string) => {
  return useQuery({
    queryKey: [...keyQuery, 'details', id],
    queryFn: () => (id ? transfersReqAPI.get(id) : Promise.resolve(undefined)),
    enabled: !!id,
  });
};

// Fetch a single transfer request by ID (for items)
export const useFetchTransferByIdItems = (id?: string) => {
  return useQuery({
    queryKey: [...keyQuery, 'items', id],
    queryFn: () => (id ? transfersReqAPI.getItems(id) : Promise.resolve(undefined)),
    enabled: !!id,
  });
};

// Create a new transfer request
export const useCreateTransfer = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: transfersReqAPI.post,
    onSuccess: () => {
      showToast('success', 'Solicitud de traslado creada correctamente.');
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};

//Export excel

export const useExportTransfer = () => {
  return useMutation({
    mutationFn: (id: string) => transfersReqAPI.export(id)
  });
};

//Export comprobante
export const useVoucherTransfer = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (id: string) => DownloadFileReqAPI.downloadPDF(id),
    onSuccess: () => {
      showToast('success', 'Certificado exitoso');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Manage a transfer request
export const useManageTransfer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ id, data }: { id: number; data: any }) => transfersReqAPI.manage(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
      },
    });
};

// Manage items transfer request
export const useManageItemsTransfer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: any }) => transfersReqAPI.manageItems(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};

// Cancel a transfer request
export const useCancelTransfer = () => {
  return useMutation<void, Error, { id: string; data: any }>({
      mutationFn: async ({ id, data }) => transfersReqAPI.cancel(id, data),
  });
};
